import en from './locale/en';
import ko from './locale/ko';
import { brandMessages } from 'shared/helpers/BrandNameHelper';

// DDukDoc: 병원 콘솔은 한국어(기본)와 영어만 eager로 싣는다.
//
// 업스트림은 40개 로케일을 전부 정적 import해서 하나의 청크(약 11MB, zstd 3MB)로
// 묶었고, 이 청크가 매 배포마다 다시 내려오고 로드 때마다 파싱됐다(2026-09-04 실측:
// 대시보드 진입 시 가장 큰 리소스). 나머지 로케일은 사용자가 실제로 그 언어를
// 고를 때만 dynamic import로 가져온다. Vite가 로케일별로 별도 청크를 만든다.
// 업스트림 index.js가 노출하던 로케일만 lazy 대상으로 둔다. locale/ 아래에는
// 업스트림도 싣지 않는 불완전 폴더(zh, am 등)가 있어 glob은 쓰지 않는다.
const LAZY_LOCALE_LOADERS = {
  ar: () => import('./locale/ar'),
  bg: () => import('./locale/bg'),
  ca: () => import('./locale/ca'),
  cs: () => import('./locale/cs'),
  da: () => import('./locale/da'),
  de: () => import('./locale/de'),
  el: () => import('./locale/el'),
  es: () => import('./locale/es'),
  fa: () => import('./locale/fa'),
  fi: () => import('./locale/fi'),
  fr: () => import('./locale/fr'),
  he: () => import('./locale/he'),
  hi: () => import('./locale/hi'),
  hu: () => import('./locale/hu'),
  id: () => import('./locale/id'),
  it: () => import('./locale/it'),
  ja: () => import('./locale/ja'),
  ml: () => import('./locale/ml'),
  lv: () => import('./locale/lv'),
  nl: () => import('./locale/nl'),
  no: () => import('./locale/no'),
  pl: () => import('./locale/pl'),
  pt_BR: () => import('./locale/pt_BR'),
  pt: () => import('./locale/pt'),
  ro: () => import('./locale/ro'),
  ru: () => import('./locale/ru'),
  sk: () => import('./locale/sk'),
  sr: () => import('./locale/sr'),
  sv: () => import('./locale/sv'),
  ta: () => import('./locale/ta'),
  th: () => import('./locale/th'),
  tr: () => import('./locale/tr'),
  uk: () => import('./locale/uk'),
  vi: () => import('./locale/vi'),
  zh_CN: () => import('./locale/zh_CN'),
  zh_TW: () => import('./locale/zh_TW'),
  is: () => import('./locale/is'),
  lt: () => import('./locale/lt'),
};

export const EAGER_LOCALES = brandMessages({ en, ko });

const loadedLocales = new Set(Object.keys(EAGER_LOCALES));
const inflight = new Map();
let globalComposer = null;

/**
 * 엔트리포인트가 createI18n 결과를 등록한다. 컴포넌트의 `this.$root.$i18n` 은
 * composition 모드에서 locale/fallbackLocale 만 노출하는 얇은 객체라 메시지 등록은
 * 여기 등록된 global composer 로 한다.
 */
export function registerI18n(i18nInstance) {
  globalComposer = i18nInstance?.global ?? i18nInstance;
}

function setLocaleMessage(locale, messages) {
  const target = globalComposer;
  if (!target || typeof target.setLocaleMessage !== 'function') {
    throw new Error(
      'i18n instance not registered; call registerI18n() in the entrypoint'
    );
  }
  target.setLocaleMessage(locale, messages);
}

/**
 * 로케일 메시지를 보장한다. eager 로케일이면 즉시 resolve, 그 외는 한 번만
 * dynamic import해서 global composer에 등록한다. 알 수 없는 코드는 등록하지 않고
 * resolve해서 호출 측이 fallbackLocale(en)로 떨어지게 둔다.
 *
 * @param {string} locale 로케일 코드 (예: 'ja', 'pt_BR')
 * @returns {Promise<void>}
 */
export async function ensureLocaleMessages(locale) {
  if (!locale || loadedLocales.has(locale)) return;
  const loader = LAZY_LOCALE_LOADERS[locale];
  if (!loader) return;
  if (!inflight.has(locale)) {
    inflight.set(
      locale,
      loader()
        .then(mod => {
          setLocaleMessage(locale, brandMessages(mod.default));
          loadedLocales.add(locale);
        })
        .finally(() => inflight.delete(locale))
    );
  }
  await inflight.get(locale);
}

/**
 * 로케일 메시지를 보장한 뒤 활성 로케일을 바꾼다.
 * 기존 `this.$root.$i18n.locale = locale` 대입 자리를 이 함수로 바꾼다.
 *
 * @param {object} i18n 컴포넌트에서 얻은 `this.$root.$i18n` (locale setter 보유)
 * @param {string} locale
 */
export async function applyLocale(i18n, locale) {
  if (!locale) return;
  await ensureLocaleMessages(locale);
  if (
    i18n?.locale &&
    typeof i18n.locale === 'object' &&
    'value' in i18n.locale
  ) {
    i18n.locale.value = locale;
  } else if (i18n) {
    i18n.locale = locale;
  }
}

export default EAGER_LOCALES;
